import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { money, fmtDateShort, isoDate, today } from "../format.js";
import {
  Async, Badge, BarList, Button, Card, Check, Chips, Empty, ErrorPanel, Field, Fields, Grid, Input,
  KPI, Modal, Select, Sep, Table, Textarea, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

/**
 * Contractor bills — invoices RECEIVED, the mirror of the Invoices page. Each
 * bill carries when it arrived, when it's due, the uploaded document, and the
 * one payment that settles it. Status (received / overdue / paid) is derived
 * server-side by the bill_statuses view.
 */
export default function Bills() {
  const toast = useToast();
  const { settings, me } = useApp();
  const canManage = !!me.permissions["bills.manage"];
  const state = useAsync(() => api.get("bills"), []);
  const [filter, setFilter] = useState("open");
  const [editing, setEditing] = useState(null); // a bill, or {} for a new one
  const [editingContractor, setEditingContractor] = useState(null); // a contractor, or {} for a new one
  const sym = settings.currency_symbol;

  return (
    <>
      <TopBar crumb="Track work" title="Contractor bills">
        <Button
          onClick={() =>
            api.download("exports/bills", "contractor-bills.csv").catch((e) => toast.push(e.message, "err"))
          }
        >
          ⤓ Export CSV
        </Button>
        {canManage && <Button onClick={() => setEditingContractor({})}>＋ Contractor</Button>}
        {canManage && (
          <Button kind="pri" onClick={() => setEditing({})}>
            ＋ Record bill
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Async state={state}>
          {(d) => {
            const list = d.list || [];
            const totals = d.totals || {};
            const rows =
              filter === "open"
                ? list.filter((b) => !b.paid_on)
                : filter === "overdue"
                ? list.filter((b) => b.display_status === "overdue")
                : filter === "paid"
                ? list.filter((b) => b.paid_on)
                : list;

            return (
              <>
                <Grid cols={4}>
                  <KPI
                    label="To pay"
                    value={money(totals.outstanding, sym)}
                    sub={`${Number(totals.open_count || 0)} open bill${Number(totals.open_count) === 1 ? "" : "s"}`}
                  />
                  <KPI
                    label="Overdue"
                    value={money(totals.overdue_amount, sym)}
                    tone={Number(totals.overdue_count) ? "danger" : undefined}
                    sub={
                      Number(totals.overdue_count)
                        ? `${totals.overdue_count} bill${Number(totals.overdue_count) === 1 ? "" : "s"} past due`
                        : "nothing past due"
                    }
                  />
                  <KPI
                    label="Paid this month"
                    value={money(totals.paid_this_month, sym)}
                    sub={`${money(totals.paid_ytd, sym)} this year`}
                  />
                  <KPI
                    label="Missing documents"
                    value={Number(totals.missing_files || 0)}
                    tone={Number(totals.missing_files || 0) ? "warn" : undefined}
                    sub={Number(totals.missing_files || 0) ? "attach the invoice PDFs" : "every bill has its invoice"}
                  />
                </Grid>

                <Grid cols="1.7fr 1fr" className="mt" style={{ alignItems: "start" }}>
                  <Card
                    bare
                    title="All bills"
                    right={
                      <Chips
                        value={filter}
                        onChange={setFilter}
                        options={[
                          { key: "open", label: "To pay" },
                          { key: "overdue", label: "Overdue" },
                          { key: "paid", label: "Paid" },
                          { key: "all", label: "All", count: list.length },
                        ]}
                      />
                    }
                  >
                    <Table
                      rows={rows}
                      onRowClick={canManage ? (b) => setEditing(b) : undefined}
                      empty={<Empty icon="▥">No contractor bills recorded</Empty>}
                      columns={[
                        {
                          key: "received_on",
                          label: "Received",
                          render: (b) => <span className="sm dim">{fmtDateShort(b.received_on)}</span>,
                        },
                        {
                          key: "contractor_name",
                          label: "Contractor",
                          render: (b) => (
                            <>
                              <b className="sm">{b.contractor_name}</b>
                              <div className="tiny muted">
                                {b.number ? <span className="num">{b.number}</span> : "no invoice #"}
                                {b.project_name ? ` · ${b.project_name}` : ""}
                              </div>
                            </>
                          ),
                        },
                        {
                          key: "due_on",
                          label: "Due",
                          render: (b) =>
                            b.paid_on ? (
                              <span className="sm dim">paid {fmtDateShort(b.paid_on)}</span>
                            ) : b.due_on ? (
                              <span className="sm" style={b.display_status === "overdue" ? { color: "var(--danger)" } : undefined}>
                                {fmtDateShort(b.due_on)}
                              </span>
                            ) : (
                              <span className="muted">—</span>
                            ),
                        },
                        {
                          key: "total",
                          label: "Total",
                          align: "right",
                          render: (b) => <b className="num">{money(b.total, sym)}</b>,
                        },
                        {
                          key: "display_status",
                          label: "Status",
                          render: (b) => (
                            <Badge
                              status={b.display_status}
                              label={b.display_status === "received" ? "To pay" : undefined}
                            />
                          ),
                        },
                        {
                          key: "file",
                          label: "",
                          align: "right",
                          render: (b) =>
                            b.file_blob_key ? (
                              <span
                                className="linky sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  api
                                    .openBlob(api.filePath(b.file_blob_key))
                                    .catch((err) => toast.push(err.message, "err"));
                                }}
                              >
                                Invoice
                              </span>
                            ) : (
                              <span className="tiny" style={{ color: "var(--warn)" }}>
                                no file
                              </span>
                            ),
                        },
                      ]}
                    />
                  </Card>

                  <div className="stack" style={{ display: "grid", gap: 14 }}>
                    <Card title="Paid by contractor">
                      {d.byContractor?.length ? (
                        <BarList
                          rows={d.byContractor.map((c) => ({ label: c.contractor, value: c.amount }))}
                          format={(v) => money(v, sym)}
                        />
                      ) : (
                        <Empty icon="▥">Nothing recorded yet</Empty>
                      )}
                      <Sep />
                      <div className="tiny muted">
                        Totals include tax — handy at year end when each contractor asks what you paid them.
                      </div>
                    </Card>

                    <Card title="Contractors" bare>
                      {(d.contractors || []).map((c) => (
                        <div
                          key={c.id}
                          style={{ padding: "11px 16px", borderBottom: "1px solid var(--line-2)", cursor: canManage ? "pointer" : "default" }}
                          onClick={canManage ? () => setEditingContractor(c) : undefined}
                        >
                          <div className="row">
                            <b className="sm">{c.name}</b>
                            <div className="grow" />
                            {c.status === "archived" && <Badge status="archived" />}
                          </div>
                          <div className="tiny muted">{c.email || "no email on file"}</div>
                        </div>
                      ))}
                      {!(d.contractors || []).length && (
                        <div className="empty sm" style={{ padding: 26 }}>
                          Add your two contractors first — every bill links to one.
                        </div>
                      )}
                    </Card>
                  </div>
                </Grid>
              </>
            );
          }}
        </Async>
      </Wrap>

      {editing && (
        <BillModal
          bill={editing}
          contractors={(state.data?.contractors || []).filter((c) => c.status === "active" || c.id === editing.contractor_id)}
          onClose={() => setEditing(null)}
          onSaved={state.reload}
        />
      )}
      {editingContractor && (
        <ContractorModal
          contractor={editingContractor}
          onClose={() => setEditingContractor(null)}
          onSaved={state.reload}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- bill modal */

function BillModal({ bill, contractors, onClose, onSaved }) {
  const { settings, projects } = useApp();
  const toast = useToast();
  const isNew = !bill.id;
  const sym = settings.currency_symbol;

  const [form, setForm] = useState({
    contractor_id: bill.contractor_id || contractors[0]?.id || "",
    number: bill.number || "",
    received_on: isoDate(bill.received_on) || today(),
    due_on: isoDate(bill.due_on) || "",
    amount: bill.amount ?? "",
    tax_amount: bill.tax_amount ?? "",
    project_id: bill.project_id || "",
    notes: bill.notes || "",
  });
  const [pay, setPay] = useState({ paid_on: today(), method: "EFT", reference: "" });
  const [payNow, setPayNow] = useState(false); // new bill: record the payment in the same go
  const [pendingFile, setPendingFile] = useState(null); // new bill: uploads right after create
  const [fileKey, setFileKey] = useState(bill.file_blob_key || null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function run(fn, message, { close = true } = {}) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      toast.push(message);
      onSaved();
      if (close) onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setError(null);
    setBusy(true);
    const body = {
      contractor_id: form.contractor_id || null,
      number: form.number,
      received_on: form.received_on,
      due_on: form.due_on || null,
      amount: Number(form.amount) || 0,
      tax_amount: Number(form.tax_amount) || 0,
      project_id: form.project_id || null,
      notes: form.notes,
    };
    try {
      if (!isNew) {
        await api.put(`bills/${bill.id}`, body);
        toast.push("Bill saved");
      } else {
        // One interaction, three writes: create, then attach the document and
        // record the payment against the id the create returned. If a follow-up
        // fails the bill still exists, so surface it rather than failing the lot.
        const created = await api.post("bills", body);
        const problems = [];
        if (pendingFile)
          await api.upload(`bills/${created.id}/file`, pendingFile).catch(() => problems.push("the file didn't upload"));
        if (payNow)
          await api.post(`bills/${created.id}/pay`, pay).catch(() => problems.push("it couldn't be marked paid"));
        if (problems.length) toast.push(`Bill recorded, but ${problems.join(" and ")} — open it to retry.`, "err");
        else toast.push(payNow ? "Bill recorded and marked paid" : "Bill recorded");
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const markPaid = () =>
    run(() => api.post(`bills/${bill.id}/pay`, pay), "Marked paid");
  const markUnpaid = () =>
    run(() => api.post(`bills/${bill.id}/unpay`, {}), "Marked unpaid");
  const remove = () => run(() => api.del(`bills/${bill.id}`), "Bill deleted");

  async function upload(file) {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const res = await api.upload(`bills/${bill.id}/file`, file);
      setFileKey(res.file_blob_key);
      toast.push("Invoice attached");
      onSaved();
    } catch (e) {
      setError(e);
    } finally {
      setUploading(false);
    }
  }

  const total = (Number(form.amount) || 0) + (Number(form.tax_amount) || 0);

  return (
    <Modal
      title={isNew ? "Record a bill" : "Edit bill"}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <Button kind="dgr" busy={busy} style={{ marginRight: "auto" }} onClick={remove}>
              Delete
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            {isNew ? (payNow ? "Record & mark paid" : "Record bill") : "Save bill"}
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}

        <Fields cols={2}>
          <Field label="Contractor">
            <Select value={form.contractor_id} onChange={(e) => set("contractor_id", e.target.value)}>
              {!contractors.length && <option value="">Add a contractor first</option>}
              {contractors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Their invoice #">
            <Input value={form.number} placeholder="INV-042" onChange={(e) => set("number", e.target.value)} />
          </Field>
        </Fields>

        <Fields cols={2}>
          <Field label="Date received">
            <Input type="date" value={form.received_on} onChange={(e) => set("received_on", e.target.value)} />
          </Field>
          <Field label="Due date">
            <Input type="date" value={form.due_on} onChange={(e) => set("due_on", e.target.value)} />
          </Field>
        </Fields>

        <Fields cols={2}>
          <Field label="Amount (before tax)">
            <Input value={form.amount} placeholder="0.00" onChange={(e) => set("amount", e.target.value)} />
          </Field>
          <Field
            label={`${settings.tax_label} charged`}
            hint={total ? `Total to pay: ${money(total, sym)}` : "Counts toward recoverable input tax"}
          >
            <Input value={form.tax_amount} placeholder="0.00" onChange={(e) => set("tax_amount", e.target.value)} />
          </Field>
        </Fields>

        <Fields cols={2}>
          <Field label="Project">
            <Select value={form.project_id} onChange={(e) => set("project_id", e.target.value)}>
              <option value="">None</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Notes">
            <Input value={form.notes} placeholder="Sprint 12, hours attached" onChange={(e) => set("notes", e.target.value)} />
          </Field>
        </Fields>

        <Field label="Their invoice (PDF or image)">
          {isNew ? (
            <div className="row">
              <Input type="file" disabled={busy} onChange={(e) => setPendingFile(e.target.files?.[0] || null)} />
              <span className="tiny muted nowrap">{pendingFile ? "attaches when you save" : "optional"}</span>
            </div>
          ) : (
            <div className="row">
              <Input type="file" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} />
              {uploading ? (
                <span className="tiny muted nowrap">Uploading…</span>
              ) : fileKey ? (
                <span
                  className="linky sm nowrap"
                  onClick={() =>
                    api.openBlob(api.filePath(fileKey)).catch((err) => toast.push(err.message, "err"))
                  }
                >
                  View
                </span>
              ) : (
                <span className="tiny muted nowrap">None attached</span>
              )}
            </div>
          )}
        </Field>

        {isNew && (
          <>
            <Sep />
            <Check
              label="Already paid — record the payment at the same time"
              checked={payNow}
              onChange={setPayNow}
            />
            {payNow && (
              <Fields cols={3}>
                <Field label="Paid on">
                  <Input type="date" value={pay.paid_on} onChange={(e) => setPay({ ...pay, paid_on: e.target.value })} />
                </Field>
                <Field label="Method">
                  <Select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                    {["EFT", "e-Transfer", "Cheque", "Credit card", "Cash", "Other"].map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Reference">
                  <Input
                    value={pay.reference}
                    placeholder="confirmation #"
                    onChange={(e) => setPay({ ...pay, reference: e.target.value })}
                  />
                </Field>
              </Fields>
            )}
          </>
        )}

        {!isNew && (
          <>
            <Sep />
            {bill.paid_on ? (
              <div className="row">
                <div className="sm">
                  Paid {fmtDateShort(bill.paid_on)}
                  {bill.paid_method ? ` by ${bill.paid_method}` : ""}
                  {bill.paid_reference ? <span className="muted num"> · {bill.paid_reference}</span> : ""}
                </div>
                <div className="grow" />
                <Button sm busy={busy} onClick={markUnpaid}>
                  Mark unpaid
                </Button>
              </div>
            ) : (
              <>
                <Fields cols={3}>
                  <Field label="Paid on">
                    <Input type="date" value={pay.paid_on} onChange={(e) => setPay({ ...pay, paid_on: e.target.value })} />
                  </Field>
                  <Field label="Method">
                    <Select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                      {["EFT", "e-Transfer", "Cheque", "Credit card", "Cash", "Other"].map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Reference">
                    <Input
                      value={pay.reference}
                      placeholder="confirmation #"
                      onChange={(e) => setPay({ ...pay, reference: e.target.value })}
                    />
                  </Field>
                </Fields>
                <div className="row">
                  <div className="grow" />
                  <Button kind="pri" busy={busy} onClick={markPaid}>
                    ✓ Mark paid
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Fields>
    </Modal>
  );
}

/* -------------------------------------------------------- contractor modal */

function ContractorModal({ contractor, onClose, onSaved }) {
  const toast = useToast();
  const isNew = !contractor.id;

  const [form, setForm] = useState({
    name: contractor.name || "",
    email: contractor.email || "",
    phone: contractor.phone || "",
    notes: contractor.notes || "",
    status: contractor.status || "active",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function run(fn, message) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      toast.push(message);
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(
      () => (isNew ? api.post("contractors", form) : api.put(`contractors/${contractor.id}`, form)),
      isNew ? "Contractor added" : "Contractor saved"
    );
  const remove = () => run(() => api.del(`contractors/${contractor.id}`), "Contractor removed");

  return (
    <Modal
      title={isNew ? "New contractor" : "Edit contractor"}
      width={480}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <Button kind="dgr" busy={busy} style={{ marginRight: "auto" }} onClick={remove}>
              Delete
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            {isNew ? "Add contractor" : "Save contractor"}
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Field label="Name">
          <Input value={form.name} placeholder="Nadia Osei" onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Fields cols={2}>
          <Field label="Email">
            <Input value={form.email} placeholder="nadia@example.com" onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
        </Fields>
        <Field label="Notes">
          <Textarea rows={2} value={form.notes} placeholder="Rate, terms, what they work on…" onChange={(e) => set("notes", e.target.value)} />
        </Field>
        {!isNew && (
          <Field label="Status" hint="Archiving hides them from new bills but keeps the history.">
            <Select value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </Select>
          </Field>
        )}
      </Fields>
    </Modal>
  );
}
